import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { login } from '../services/auth';
import { useAuthStore } from '../store/authStore';
import { getErrorMessage } from '../services/api';
import OptionToggle from '../components/common/OptionToggle';

export default function Login() {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await login({ email, password, rememberMe });

      if (response.success && response.data) {
        setAuth(response.data.user, {
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          expiresIn: response.data.expiresIn,
        });
        // Rediriger vers returnUrl si présent, sinon vers dashboard
        navigate(returnUrl || '/dashboard');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="pub-page min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="text-3xl font-bold text-primary-600">
            Splinity
          </Link>
          <h1 className="pub-title mt-6 text-2xl font-bold">
            {t('login.title')}
          </h1>
          <p className="pub-muted mt-2">
            {t('login.noAccount')}{' '}
            <Link to="/register" className="text-primary-600 hover:underline">
              {t('login.signUp')}
            </Link>
          </p>
        </div>

        <div className="pub-card p-8">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label={t('login.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('login.emailPlaceholder')}
              required
              autoComplete="email"
            />

            <div className="relative">
              <Input
                label={t('login.password')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="pub-subtle absolute right-3 top-8 hover:text-primary-600 transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>

            <div className="flex items-center justify-between">
              <OptionToggle
                variant="row"
                selected={rememberMe}
                onToggle={() => setRememberMe(!rememberMe)}
              >
                {t('login.rememberMe')}
              </OptionToggle>
              <Link to="/forgot-password" className="text-sm text-primary-600 hover:underline">
                {t('login.forgotPassword')}
              </Link>
            </div>

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              size="lg"
              isLoading={isLoading}
            >
              {t('login.submit')}
            </Button>
          </form>
        </div>

        <p className="pub-subtle mt-8 text-center text-sm">
          <Link to="/" className="hover:text-primary-600">
            &larr; {t('login.backToHome')}
          </Link>
        </p>
      </div>
    </div>
  );
}
